import { fetchExperienceListFacets } from 'actions/ExperienceActions'
import Places from 'constants/places'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { arrayToObject } from 'lib/array/arrayUtils'
import getObjectKey from 'lib/object/getObjectKey'
import { useEffect, useMemo } from 'react'
import { defineMessages, useIntl } from 'react-intl'
import { type ExpListDefinition, useCategoryCarousels } from '../ExperiencesPage/ExperiencesPageHardcodedLists'
import useMostPopulatedSearchExperienceFacets from './useMostPopulatedSearchExperienceFacets'

type ExperienceThemeChildCategory = App.ExperienceItemCategory & { count: number }

export type ExperienceTheme =
  | { id: string; title: string; count: number; type: 'category'; categoryCode: number; children: Array<ExperienceThemeChildCategory> }
  | { id: string; title: string; count: number; type: 'collection'; collectionId: string }

const GLOBAL_FACET_KEY = getObjectKey({ placeId: Places.Anywhere.id })

const SEARCH_THEME_TITLES_BY_CATEGORY_CODE = defineMessages({
  1: {
    id: 't13R4qnBYme3l2DJRUDR',
    defaultMessage: 'Wine & Dine',
    description: 'Experiences search theme label for food and drink activities across filter surfaces',
  },
  9: {
    id: 'KB1OwMbHxVWfeGg6WUhz',
    defaultMessage: 'Top Activities',
    description: 'Experiences search theme label for popular activities across filter surfaces',
  },
  18: {
    id: 'WwGDioch9tJulRLEOSNc',
    defaultMessage: 'See the Sights',
    description: 'Experiences search theme label for sightseeing activities across filter surfaces',
  },
  48: {
    id: 'pNSjxH4oesX-kf8BLzm9',
    defaultMessage: 'Day Tours',
    description: 'Experiences search theme label for day tour activities across filter surfaces',
  },
})

export function getSearchThemeTitle(
  intl: ReturnType<typeof useIntl>,
  categoryCode: number,
  fallbackTitle: string,
): string {
  if (!(categoryCode in SEARCH_THEME_TITLES_BY_CATEGORY_CODE)) {
    return fallbackTitle
  }

  return intl.formatMessage(
    SEARCH_THEME_TITLES_BY_CATEGORY_CODE[categoryCode as keyof typeof SEARCH_THEME_TITLES_BY_CATEGORY_CODE],
  )
}

function useExperienceThemes(place?: App.Place): Array<ExperienceTheme> {
  const intl = useIntl()
  const dispatch = useAppDispatch()
  const searchFacets = useAppSelector((state) => state.experience.experienceListFacets)
  const categoryCarousels = useCategoryCarousels()
  const {
    primaryCategories,
    categories: categoryFacets,
    collections: placeCollections,
  } = useMostPopulatedSearchExperienceFacets(place, {
    rankCategoriesByRelevance: true,
    prioritiseSelectedCategories: true,
  })

  useEffect(() => {
    dispatch(fetchExperienceListFacets(Places.Anywhere.id, {}))
  }, [dispatch])

  const countByCode = useMemo(
    () => new Map(primaryCategories.map((item) => [item.parent.id, item.count])),
    [primaryCategories],
  )

  const categoryFacetsById = useMemo(
    () => arrayToObject(categoryFacets, (facet) => facet.categoryId),
    [categoryFacets],
  )

  const globalCollections = useMemo(() => {
    return searchFacets[GLOBAL_FACET_KEY]?.facets?.collection ?? []
  }, [searchFacets])

  const placeCollectionCountById = useMemo(
    () => new Map(placeCollections.map((collection) => [collection.collectionId, collection.count])),
    [placeCollections],
  )

  return useMemo(() => {
    const categoryCarouselsByCode = new Map(
      categoryCarousels
        .filter((carousel): carousel is ExpListDefinition & { code: number } => carousel.code !== undefined)
        .map((carousel) => [carousel.code, carousel]),
    )

    const categoryThemes: Array<ExperienceTheme> = primaryCategories
      .map((category) => {
        const carousel = categoryCarouselsByCode.get(category.parent.id)
        if (!carousel) {
          return
        }

        return {
          id: `category-${carousel.code}`,
          title: getSearchThemeTitle(intl, carousel.code, carousel.title),
          count: countByCode.get(carousel.code) ?? 0,
          type: 'category' as const,
          categoryCode: carousel.code,
          children: category.children.map((child) => ({
            ...child,
            count: categoryFacetsById[child.id]?.count ?? 0,
          })),
        }
      })
      .filter((theme): theme is Extract<ExperienceTheme, { type: 'category' }> => !!theme)

    const collectionThemes: Array<ExperienceTheme> = globalCollections.map((collection) => ({
      id: `collection-${collection.collectionId}`,
      title: collection.theme,
      count: placeCollectionCountById.get(collection.collectionId) ?? 0,
      type: 'collection' as const,
      collectionId: collection.collectionId,
    }))

    return [...categoryThemes, ...collectionThemes]
  }, [categoryCarousels, categoryFacetsById, countByCode, globalCollections, intl, placeCollectionCountById, primaryCategories])
}

export default useExperienceThemes
